/**
 * Shared harness logic used by both the Node (`harness.ts`) and browser
 * (`harness.browser.ts`) implementations.  Only depends on `@gotti/core`.
 *
 * Each environment supplies its own `serverProc` and `clientProc` via
 * `buildTestHarness()`.  All routing, lifecycle, and convenience helpers
 * live here — one source of truth.
 */
import { Process, System, type ReceivedMessage } from '@gotti/core';
import { runAsRegisteringClient } from './focus-scoped-input.js';

// ── Types ────────────────────────────────────────────────────────────────────

// Server→client delivery. `clientId` (optional) lets a SHARED server target a
// specific client in a multi-client harness: when set, only that client's
// matching system receives the message; when absent, every client does (the
// broadcast semantics of `dispatchToAllClients`). Single-client harnesses
// ignore it entirely.
export type ServerToClientRouter = (msg: ReceivedMessage & { to?: string | number }, clientId?: string | number) => void;
export type ServerToClientRequester = (req: { to?: string | number; type: string | number; data: any }) => Promise<any>;
export type ClientToServerRequester = (req: { to?: string | number; type: string | number; data: any }) => Promise<any>;
// Symmetric to ServerToClientRouter — client→server fire-and-forget. Used
// by the stub client connector's `send` decoder to surface
// `dispatchToServer` / `dispatchToArea` calls into a test-observable channel.
// `clientId` identifies the SENDING client so a shared server can route N
// distinct senders to `onClientMessage(clientId, message)` with the right id.
export type ClientToServerRouter = (msg: { to?: string | number; type: string | number; data: any; areaId?: string; clientId?: string | number }) => void;

export type TestHarness = {
  serverProc: Process;
  /** Client process of index 0 — back-compat alias of `clientProcs[0]`. */
  clientProc: Process;
  /** All booted client processes, index-aligned with `addClientSystem`'s index. */
  clientProcs: Process[];
  /** Distinct join id per client process (`player-1`, `player-2`, …). */
  clientIds: (string | number)[];
  /**
   * Simulate a client disconnecting — the same event the sandbox UI fires when
   * you close a client's canvas window. Fires every server system's
   * `onClientLeft(clientId)` cleanup hook and stops that client from ticking.
   * Client indices of the REMAINING clients are unchanged.
   */
  disconnectClient: (clientId: string | number) => void;
  addServerSystem: <S>(sys: S | (new () => S)) => S;
  /** Add a client system to a specific client process (default index 0). */
  addClientSystem: <S>(sys: S | (new () => S), clientIndex?: number) => S;
  start: () => void;
  stop: () => void;
  registerRoute: (to: string | number, handler: (msg: ReceivedMessage & { to?: string | number }) => void) => void;
  registerRequestRoute: (to: string | number, handler: (req: { to: string | number; type: string | number; data: any }) => Promise<any>) => void;
  registerServerRequestRoute: (to: string | number, handler: (req: { to: string | number; type: string | number; data: any }) => Promise<any>) => void;
  /**
   * One-way client→server dispatch interceptor — symmetric counterpart to
   * `registerRoute` (server→client). Required so tests can assert on
   * messages a system sent via `this.dispatchToServer(...)` /
   * `this.dispatchToArea(...)`, which go through `connector.send([...])`
   * (binary path) rather than `connector.requestServer(...)`. The handler
   * receives the originating client's id as a second argument so a shared
   * server can attribute the message to the correct sender.
   */
  registerServerRoute: (to: string | number, handler: (msg: { to?: string | number; type: string | number; data: any }, clientId?: string | number) => void) => void;
  tick: (delta?: number) => void;
  forwardToClient: ServerToClientRouter;
  forwardRequestToClient: ServerToClientRequester;
  forwardRequestToServer: ClientToServerRequester;
  /**
   * Low-level entry-point the stub client connector's `send` decoder calls.
   * If a custom route is registered for `msg.to` it fires; otherwise the
   * message is delivered to the matching server system's
   * `onClientMessage` handler (same fallback pattern as forwardToClient).
   */
  forwardToServer: ClientToServerRouter;
  destroy: () => void;
};

// ── Area server stub ─────────────────────────────────────────────────────────

export function createAreaServerStub(
  getForwardToClient: () => ServerToClientRouter,
  getForwardRequestToClient: () => ServerToClientRequester,
): Record<string, any> {
  return {
    id: 'test-area-1',
    config: { options: { areaFile: { type: 'test-area' } } },
    gameConfigData: {},
    areaConfigData: {},
    gameServerData: {},
    areaServerData: {},
    areaState: { data: {} },
    // Broadcast — no clientId, so forwardToClient fans out to every client.
    dispatchToAllClients: (msg: any) => getForwardToClient()(msg),
    // Targeted — pass the clientId so a multi-client harness delivers to only
    // that client (single-client harnesses ignore it and deliver to the one).
    dispatchToClient: (id: any, msg: any) => getForwardToClient()(msg, id),
    dispatchToClients: (ids: any[], msg: any) => {
      for (const id of ids ?? []) getForwardToClient()(msg, id);
    },
    dispatchToAreas: () => {},
    dispatchToArea: () => {},
    requestClient: (_id: any, req: any) => getForwardRequestToClient()(req),
    requestArea: async () => undefined,
    addClientToArea: () => true,
    replaceClientArea: () => true,
    removeClientFromArea: () => true,
    dispatchToMaster: () => {},
    requestMaster: async () => undefined,
    // Dynamic area lifecycle. Real AreaServer implements these; the stub must
    // too, because ServerProcess.bindServerMethods binds every ServerMethodsApi
    // member UNCONDITIONALLY (`obj1.createArea.bind(obj1)`) — a missing one
    // throws `Cannot read properties of undefined (reading 'bind')` and aborts
    // the whole bind, leaving later methods (setState, createChannel, …)
    // unbound. Return shapes mirror the real signatures (createArea →
    // { areaId }, destroyArea → void); the id matches this stub's 'test-area-1'.
    createArea: async () => ({ areaId: 'test-area-1' }),
    destroyArea: async () => {},
    setState: () => {},
    dispatchRaw: () => {},
    createChannel: () => {},
    destroyChannel: () => {},
    addClientToChannel: () => {},
    removeClientFromChannel: () => {},
    dispatchToChannel: () => {},
    requestChannel: async () => [],
    getClientsInChannel: () => [],
  };
}

// ── Type guards ──────────────────────────────────────────────────────────────

export function hasOnServerMessage(x: unknown): x is { onServerMessage: (message: ReceivedMessage) => void } {
  return !!x && typeof (x as any).onServerMessage === 'function';
}
export function hasOnServerRequest(x: unknown): x is { onServerRequest: (req: any) => Promise<any> } {
  return !!x && typeof (x as any).onServerRequest === 'function';
}
export function hasOnClientRequest(x: unknown): x is { onClientRequest: (req: any) => Promise<any> } {
  return !!x && typeof (x as any).onClientRequest === 'function';
}
export function hasOnClientMessage(x: unknown): x is { onClientMessage: (message: ReceivedMessage) => void } {
  return !!x && typeof (x as any).onClientMessage === 'function';
}

// ── buildTestHarness ─────────────────────────────────────────────────────────
// Takes pre-constructed serverProc & clientProc, wires up all routing.

export function buildTestHarness(
  serverProc: Process,
  clientProcOrProcs: Process | Process[],
  clientIds?: (string | number)[],
): TestHarness {
  // Normalize to an array; a single Process keeps the legacy one-client path.
  const clientProcs = Array.isArray(clientProcOrProcs) ? clientProcOrProcs : [clientProcOrProcs];
  const ids: (string | number)[] = clientProcs.map((_, i) => clientIds?.[i] ?? `player-${i + 1}`);
  const clientIdToIndex = new Map<string | number, number>();
  ids.forEach((id, i) => clientIdToIndex.set(id, i));

  // Client systems are kept PER CLIENT INDEX (every booted client gets its own
  // instance of each client system, exactly as N split-screen clients do live).
  const clientSystemsByIndex = new Map<number, Map<string | number, System<any>>>();
  const clientSystemsFor = (idx: number): Map<string | number, System<any>> => {
    let m = clientSystemsByIndex.get(idx);
    if (!m) { m = new Map(); clientSystemsByIndex.set(idx, m); }
    return m;
  };
  const serverSystems = new Map<string | number, System<any>>();
  // Indices of clients that have disconnected — skipped on tick so they stop
  // participating (the server-side cleanup already ran via onClientLeft).
  const disconnected = new Set<number>();
  const routes = new Map<string | number, (msg: ReceivedMessage & { to?: string | number }) => void>();
  const requestRoutes = new Map<string | number, (req: any) => Promise<any>>();
  const serverRequestRoutes = new Map<string | number, (req: any) => Promise<any>>();
  const serverRoutes = new Map<string | number, (msg: any, clientId?: string | number) => void>();

  const forwardToClient: ServerToClientRouter = (msg, clientId) => {
    const target = msg.to;
    if (target === undefined || target === null) return;
    // A custom route (registerRoute) intercepts once, regardless of client —
    // preserves the existing single-delivery contract tests rely on.
    const custom = routes.get(target);
    if (custom) return custom(msg);
    // Pick which client(s) receive it. With a single client, ALWAYS deliver to
    // it (targeted == broadcast) so legacy behaviour is byte-identical. With
    // many clients: a known clientId routes to exactly that client; an absent
    // or unknown id broadcasts (the `dispatchToAllClients` semantics).
    const indices =
      clientProcs.length <= 1
        ? [...clientSystemsByIndex.keys()]
        : (clientId !== undefined && clientId !== null && clientIdToIndex.has(clientId)
            ? [clientIdToIndex.get(clientId) as number]
            : [...clientSystemsByIndex.keys()]);
    for (const idx of indices) {
      const sys = clientSystemsByIndex.get(idx)?.get(target);
      if (hasOnServerMessage(sys)) {
        sys.onServerMessage({ type: msg.type, data: msg.data });
      }
    }
  };

  // Find a client system by name across the per-index maps (lowest index
  // first). For a single client this is just client 0's system; for many, a
  // server→client request resolves to the first client exposing the handler.
  const findClientSystem = (target: string | number): System<any> | undefined => {
    for (const idx of [...clientSystemsByIndex.keys()].sort((a, b) => a - b)) {
      const s = clientSystemsByIndex.get(idx)?.get(target);
      if (s) return s;
    }
    return undefined;
  };

  const forwardRequestToClient: ServerToClientRequester = async (req) => {
    const target = req.to;
    if (target === undefined || target === null) throw new Error('forwardRequestToClient requires req.to');
    const normalized = { to: target, type: req.type, data: req.data };
    const custom = requestRoutes.get(target);
    if (custom) return custom(normalized);
    const sys = findClientSystem(target);
    if (!hasOnServerRequest(sys)) {
      throw new Error(`No client system '${String(target)}' with onServerRequest`);
    }
    return sys.onServerRequest(normalized);
  };

  const forwardRequestToServer: ClientToServerRequester = async (req) => {
    const target = req.to;
    if (target === undefined || target === null) throw new Error('forwardRequestToServer requires req.to');
    const normalized = { to: target, type: req.type, data: req.data };
    const custom = serverRequestRoutes.get(target);
    if (custom) return custom(normalized);
    const sys = serverSystems.get(target);
    if (!hasOnClientRequest(sys)) {
      throw new Error(`No server system '${String(target)}' with onClientRequest`);
    }
    return sys.onClientRequest(normalized);
  };

  // Mirrors forwardToClient — one-way client→server delivery. Called by the
  // stub client connector's `send` decoder for `dispatchToServer` /
  // `dispatchToArea` etc. Falls back to delivering to the server system's
  // `onClientMessage` handler when no custom route is registered.
  const forwardToServer: ClientToServerRouter = (msg) => {
    const target = msg.to;
    if (target === undefined || target === null) return;
    const custom = serverRoutes.get(target);
    // Pass the originating client's id so a 2-arg `onClientMessage(clientId,
    // message)` route attributes the message to the right sender.
    if (custom) return custom(msg, msg.clientId);
    const sys = serverSystems.get(target);
    if (hasOnClientMessage(sys)) {
      sys.onClientMessage({ type: msg.type, data: msg.data });
    }
  };

  const addServerSystem = <S,>(sysOrCtor: S | (new () => S)): S => {
    const added = (typeof sysOrCtor === 'function')
      ? serverProc.addSystem(sysOrCtor as unknown as new () => System<any>)
      : serverProc.addSystem(sysOrCtor as unknown as System<any>);
    const sys = added as System<any>;
    (sys as any).dispatchToAllClients = (msg: any) => forwardToClient(msg);
    (sys as any).requestClient = async (_playerId: any, message: any) => forwardRequestToClient(message);
    serverSystems.set(sys.name as string | number, sys);
    return sys as unknown as S;
  };

  const addClientSystem = <S,>(sysOrCtor: S | (new () => S), clientIndex = 0): S => {
    const proc = clientProcs[clientIndex] ?? clientProcs[0];
    const added = (typeof sysOrCtor === 'function')
      ? proc.addSystem(sysOrCtor as unknown as new () => System<any>)
      : proc.addSystem(sysOrCtor as unknown as System<any>);
    const sys = added as System<any>;
    (sys as any).isNetworked = true;
    clientSystemsFor(clientIndex).set(sys.name as string | number, sys);
    return sys as unknown as S;
  };

  const registerRoute = (to: string | number, handler: (msg: ReceivedMessage & { to?: string | number }) => void) => {
    routes.set(to, handler);
  };
  const registerRequestRoute = (to: string | number, handler: (req: any) => Promise<any>) => {
    requestRoutes.set(to, handler);
  };
  const registerServerRequestRoute = (to: string | number, handler: (req: any) => Promise<any>) => {
    serverRequestRoutes.set(to, handler);
  };
  const registerServerRoute = (
    to: string | number,
    handler: (msg: { to?: string | number; type: string | number; data: any }, clientId?: string | number) => void,
  ) => {
    serverRoutes.set(to, handler);
  };

  // Lazy auto-start: the loops start on the FIRST call to start() or
  // tick(). This makes the canonical test pattern (beforeEach adds
  // systems, then calls tick) work without the test having to remember
  // to call start() — by the time tick() fires, all systems registered
  // in beforeEach are present, so startLoop's per-system onStart sweep
  // covers them all. Matches the production "add systems, then start
  // the loop" ordering.
  let autoStarted = false;
  const start = () => {
    if (autoStarted) return;
    autoStarted = true;
    serverProc.startLoop();
    // Tag each client's onStart-registered window key listeners with that
    // client's index, so focus-scoped-input routes a keypress to ONLY the
    // focused client — no per-system isFocused gate needed in generated code.
    clientProcs.forEach((cp, i) => runAsRegisteringClient(i, () => cp.startLoop()));
  };
  // One shared server, N clients ticking in a fixed index order (0..N-1) so the
  // sequence is reproducible across runs.
  const tick = (delta = 1) => {
    start();
    serverProc.update(delta);
    // Same registering-client context for onUpdate, in case a system registers
    // its key listener lazily on the first tick rather than in onStart.
    clientProcs.forEach((cp, i) => {
      if (disconnected.has(i)) return; // a disconnected client no longer ticks
      runAsRegisteringClient(i, () => cp.update(delta));
    });
  };

  const disconnectClient = (clientId: string | number): void => {
    const id = String(clientId);
    // Fire the real server-side disconnect cleanup on every server system —
    // identical to the live runtime's onClientLeft when a client window closes.
    for (const sys of serverSystems.values()) {
      (sys as unknown as { onClientLeft?: (id: string) => void }).onClientLeft?.(id);
    }
    const idx = ids.findIndex((x) => String(x) === id);
    if (idx >= 0) disconnected.add(idx);
  };

  const stop = () => {
    serverProc.stopLoop();
    for (const cp of clientProcs) cp.stopLoop();
  };

  const destroy = () => {
    serverProc.clear();
    for (const cp of clientProcs) cp.clear();
  };

  return {
    serverProc,
    clientProc: clientProcs[0],
    clientProcs,
    clientIds: ids,
    disconnectClient,
    addServerSystem,
    addClientSystem,
    start,
    stop,
    registerRoute,
    registerRequestRoute,
    registerServerRequestRoute,
    registerServerRoute,
    tick,
    forwardToClient,
    forwardRequestToClient,
    forwardRequestToServer,
    forwardToServer,
    destroy,
  };
}

// ── setupSystemPair convenience ──────────────────────────────────────────────

export function setupSystemPair<S, C>(
  createHarness: () => TestHarness,
  opts: { server: S | (new () => S); client: C | (new () => C) },
): { harness: TestHarness; server: S; client: C } {
  const harness = createHarness();
  const server = harness.addServerSystem(opts.server);
  const client = harness.addClientSystem(opts.client);
  return { harness, server, client };
}
